import {
    InputSearchBpl,
    Location,
    LocationStatus,
    OpenSearchType,
    WildCardTitleSearchConnection,
} from 'src/types/schema/graphql'
import { create } from 'zustand'
import { devtools } from 'zustand/middleware'
import searchBPLLocations from '../utils/graph/searchBPLLocations'
import { RequestInput, apiRequest } from 'src/api/apiRequest'
import getDistanceBetweenCoords from 'src/util/helpers/getDistanceBetweenCoords'
import { Coordinate } from 'react-map-gl/src/components/draggable-control'

interface IDistanceLocation {
    location: Location
    distance: number
    directDistance?: number
}
interface IuseLocationSlugStore {
    locationData: Location
    isThisYourBreweryModalIsOpen: boolean
    setLocation: (location: Location) => void
    getDistanceFromLocation: (startLoc: Location, endLoc: Location) => Promise<number>
    resetState: () => void
    getLocations: (location: Location) => Promise<void>
    suggestedLocations: IDistanceLocation[]
    setSuggestedLocations: (suggestedLocations: IDistanceLocation[]) => void
    setIsThisYourBreweryModalIsOpen: (isOpen: boolean) => void
}

const fetchDirectionsURL: (startLoc: Location, endLoc: Location) => string = (startLoc, endLoc) => {
    return `https://api.mapbox.com/directions/v5/mapbox/driving/${startLoc?.address?.geo?.lon},${startLoc?.address?.geo?.lat};${endLoc?.address?.geo?.lon},${endLoc?.address?.geo?.lat}?steps=true&geometries=geojson&access_token=${process.env.NEXT_PUBLIC_MAPBOX_TOKEN}}`
}

export const useLocationSlugStore = create<IuseLocationSlugStore>()(
    devtools(
        (set, get) => ({
            locationData: null,
            isThisYourBreweryModalIsOpen: false,
            setLocation: (locationData) => set({ locationData }),
            setIsThisYourBreweryModalIsOpen: (isOpen) =>
                set({ isThisYourBreweryModalIsOpen: isOpen }),
            suggestedLocations: [],
            setSuggestedLocations: (suggestedLocations) =>
                set((state) => ({
                    suggestedLocations: [...state.suggestedLocations, ...suggestedLocations].slice(
                        0,
                        10
                    ),
                })),
            getDistanceFromLocation: async (startLoc, endLoc) => {
                const query = await fetch(fetchDirectionsURL(startLoc, endLoc), { method: 'GET' })
                const json = await query.json()
                const data = json.routes[0]
                return data.distance
            },
            resetState: () =>
                set({
                    suggestedLocations: [],
                }),

            getLocations: async (location) => {
                const input: RequestInput<InputSearchBpl> = {
                    query: searchBPLLocations,
                    args: {
                        input: {
                            limit: 10,
                            typeFilters: [OpenSearchType.Location],
                            search: {
                                wildcardText: '',
                                searchFieldsLocations: {
                                    geoRadiusSearch: {
                                        geoRadius: 50,
                                        geoPoint: {
                                            lat: location?.address?.geo?.lat,
                                            lon: location?.address?.geo?.lon,
                                        },
                                    },
                                    status: LocationStatus.OpenForBusiness,
                                },
                            },
                            nextToken: null,
                        },
                    },
                    file: 'useLocationSlugStore.ts',
                    endpoint: 'searchBpl',
                    publicQuery: true,
                    noAlerts: true,
                }
                const { data } = await apiRequest<WildCardTitleSearchConnection, InputSearchBpl>(
                    input
                )

                if (data) {
                    const filteredLocations: Location[] = data?.items.filter((item: Location) => {
                        return location?.id !== item.id
                    })
                    const suggestedLocations = filteredLocations
                        .map((item) => {
                            return {
                                location: item,
                                distance: getDistanceBetweenCoords([
                                    [
                                        location?.address?.geo.lat,
                                        location?.address?.geo.lon,
                                    ] as Coordinate,
                                    [item?.address?.geo.lat, item?.address?.geo.lon] as Coordinate,
                                ]),
                            }
                        })
                        .sort((a, b) => a.distance - b.distance)
                        .slice(0, 10)

                    get().setSuggestedLocations(suggestedLocations)
                }
            },
        }),
        { name: `useLocationSlugStore` }
    )
)
